// Shared "your usage" card, backed by the usage summary endpoint.
// Used on the Dashboard (Home), Profile, and (compact) on feature pages.
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Gauge, Infinity as InfinityIcon, Lock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { FEATURE_LABELS } from "@/components/UpgradeModal";

export interface UsageFeature {
  used: number;
  limit: number | null; // null == unlimited
  remaining: number | null;
  period: string;
  scope?: "personal" | "org";
}
export interface UsageSummary {
  tier: string;
  personalTier?: string;
  organization?: {
    id: string;
    name: string;
    plan: string;
    billingActive: boolean;
    includedOwnerTier?: string;
  } | null;
  features: Record<string, UsageFeature>;
}

const FEATURE_ORDER = ["compatibility_match", "consultation_booking", "kundali_pdf", "tarot_reading"];

export function useUsageSummary(enabled: boolean = true) {
  return useQuery<UsageSummary>({
    queryKey: ["/api/usage/me"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/usage/me");
      if (!res.ok) throw new Error("failed to load usage");
      return (await res.json()) as UsageSummary;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function UsageRow({ featureKey, feature }: { featureKey: string; feature: UsageFeature }) {
  const unlimited = feature.limit === null;
  const locked = !unlimited && (feature.limit ?? 0) <= 0;
  const pct = unlimited
    ? 0
    : feature.limit && feature.limit > 0
      ? Math.min(100, Math.round((feature.used / feature.limit) * 100))
      : 100;
  const label = FEATURE_LABELS[featureKey] ?? featureKey;
  const isDaily = feature.period.length > 7; // YYYY-MM-DD vs YYYY-MM
  return (
    <div data-testid={`usage-${featureKey}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          {unlimited ? (
            <>
              <InfinityIcon className="h-3.5 w-3.5 text-primary" />
              Unlimited
            </>
          ) : locked ? (
            <>
              <Lock className="h-3.5 w-3.5" />
              Not in your plan
            </>
          ) : (
            <>
              {feature.used} / {feature.limit}
              <span className="text-muted-foreground/60">{isDaily ? " today" : " this month"}</span>
              {feature.scope === "org" && <span className="text-muted-foreground/60"> · shared</span>}
            </>
          )}
        </span>
      </div>
      {!unlimited && !locked && (
        <Progress value={pct} className="h-2 bg-secondary/60" data-testid={`usage-progress-${featureKey}`} />
      )}
    </div>
  );
}

export default function UsageMeterCard({
  enabled = true,
  title = "Your usage",
}: {
  enabled?: boolean;
  title?: string;
}) {
  const { data } = useUsageSummary(enabled);
  if (!data) return null;

  const keys = FEATURE_ORDER.filter((k) => data.features[k]);
  if (keys.length === 0) return null;
  const hasPersonalUpgradeOpportunity =
    (data.personalTier ?? data.tier) !== "celestial" &&
    keys.some((key) => data.features[key].scope !== "org" && data.features[key].limit !== null);

  return (
    <Card className="border-border/30 bg-card/80 backdrop-blur-sm" data-testid="usage-meter-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
        {data.organization?.billingActive && (
          <p className="text-xs text-muted-foreground" data-testid="usage-organization-coverage">
            Shared {data.organization.plan[0].toUpperCase() + data.organization.plan.slice(1)} organization coverage via {data.organization.name}. Personal billing tier: {data.personalTier ?? data.tier}; effective access: {data.tier}.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        {keys.map((key) => (
          <UsageRow key={key} featureKey={key} feature={data.features[key]} />
        ))}
        {hasPersonalUpgradeOpportunity && (
          <div className="pt-1">
            <Link href="/pricing">
              <Button
                variant="outline"
                size="sm"
                className="w-full border-primary/40 hover:bg-primary/10"
                data-testid="button-usage-upgrade"
              >
                Upgrade for more
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
